import { createStore } from 'vuex';
import axios from 'axios';

import { createToken } from './csrf';
import msg from '../messages';
import { getQueryString } from '../query';
import { parseIosViewPrice } from '../iosview';
import Tracker from './tracking/tracker';

// Polyfills for IE
require('es6-promise').polyfill();
require('es6-object-assign').polyfill();

const store = createStore({
    state: {
        originalProduct: window.App.product,
        contact: window.App.contact,
        braintreeEnvironment: window.App.braintreeEnvironment,
        sessionId: window.App.sessionId,
        supportEmail: window.App.supportEmail,
        isUpsell: window.App.isUpsell,
        device_data: window.navigator.sayswho + ' ' + window.navigator.platform,
        braintreeDeviceData: null,
        defaultPaymentMethod: window.App.defaultPaymentMethod,
        defaultPaymentMethodToken: window.App.defaultPaymentMethodToken,
        paymentMethodToken: window.App.defaultPaymentMethodToken,
        originalUrl: window.App.originalUrl,
        referer: window.App.referer,
        originalDate: window.App.originalDate,
        domainSlug: window.App.domainSlug,
        paypalClientId: window.App.paypalClientId,
        initialCurrency: window.App.initialCurrency,
        appEnvironment: window.App.appEnvironment,
        currency: null,
        enforceHardDisplay: false,
        removeOriginalPrice: window.App.removeOriginalPrice,
        removeLowestBeforePrice: window.App.removeLowestBeforePrice,
        removeScarcity: window.App.removeScarcity,
        checkoutPaymentAttempts: 0,
        immediatePaymentAttempts: 0,
        updatePaymentMethodAttempts: 0,
        retryAttempt: false,
        liabilityShifted: null,
        liabilityShiftPossible: null,
        verifyStatus: null,
        countryOfIssuance: window.App.country_of_issuance,
        issuingBank: window.App.issuing_bank,
        cardType: window.App.card_type,
        hidePaymentPlan: window.App.hpp,

        progressTrackingTriggered: {
            product_page_loaded: [],
            first_buy_buttons_are_seen: [],
            checkout_buy_button_is_clicked: [],
            checkout_step_1_complete: [],
            upsell_buy_button_is_clicked: [],
            cc_number_entered: [],
            paypal_button_click: [],
            apple_pay_button_click: [],
            purchase_complete: [],
            apple_pay_available: [],
            checkout_started: [],
            added_payment_info: [],
        },

        popup: {
            isOpen: false,
            step: 1,
            errorCode: null,
            makeEmailFieldInvalid: false,
        },
        applePayAvailable: false,
        processingCheckout: false,
        processingImmediatePayment: false,

        lastCheckoutPaymentMethod: null,

        paymentType: 'now',
        emailInput: '',

        overrideFields: {},
        customOrderFields: {},

        salesButtonSeen: false,
        salesButtonVisible: false,

        collectedLead: {
            email: null,
            already_purchased: null,
        },

        is_sca: true,
        lifetimeUpdates: null,

        isIosBackButton: true,

        remainingSpots: null,
    },
    getters: {
        product: (state) => {
            return Object.assign({}, state.originalProduct, state.overrideFields);
        },
        formData: (state) => {
            let data = {
                psh: state.sessionId,
                payment_type: state.paymentType,
                email: state.emailInput,
                device_data: state.device_data,
                braintree_device_data: state.braintreeDeviceData,
                override_fields: state.overrideFields,
                custom_order_fields: state.customOrderFields,
                original_url: state.originalUrl,
                original_date: state.originalDate,
                currency: state.currency,
                ref: state.referer,
                is_sca: state.is_sca,
            };

            if (state.lifetimeUpdates) {
                data.product_slug = state.lifetimeUpdates?.productSlug;
            }

            return data;
        },
    },
    mutations: {
        setProduct(state, product) {
            if (!product?.price || !window.App.isIosView) {
                state.originalProduct = product;
                return;
            }
            const params = getQueryString();
            // fallback to original product if either of the price params is not present
            if (!params?.oldPrice || !params?.newPrice) {
                state.originalProduct = product;
                return;
            }
            const oldPrice = parseIosViewPrice(params.oldPrice);
            const newPrice = parseIosViewPrice(params.newPrice);
            // fallback to original product if either of the price params is not valid or the currency is not the same
            if (
                !oldPrice ||
                !newPrice ||
                oldPrice.currency !== newPrice.currency ||
                oldPrice.currencySymbol !== newPrice.currencySymbol
            ) {
                state.originalProduct = product;
                return;
            }
            product.price.iosview = {
                oldPrice,
                newPrice,
                currency: oldPrice.currency,
                currencySymbol: oldPrice.currencySymbol,
                conversionRate: oldPrice.price / product.price.original.value,
            };
            state.originalProduct = product;
        },
        setPaymentType(state, type) {
            state.paymentType = type;
        },
        setEmailInput(state, value) {
            state.emailInput = value;
        },
        openCheckout(state, payload) {
            state.popup.isOpen = true;

            if (!payload) return;

            if (payload.step !== undefined) {
                state.popup.step = payload.step;
            }

            if (payload.errorCode !== undefined) {
                state.popup.errorCode = payload.errorCode;
            }
        },
        closeCheckout(state) {
            state.popup.isOpen = false;
            state.popup.makeEmailFieldInvalid = false;
        },
        makeEmailFieldInvalid(state, payload) {
            state.popup.makeEmailFieldInvalid = payload;
        },
        clearErrorCode(state) {
            state.popup.errorCode = null;
        },
        applePayAvailable(state) {
            state.applePayAvailable = true;
        },
        setProcessingCheckout(state, value) {
            state.processingCheckout = value;
        },
        setProcessingImmediatePayment(state, value) {
            state.processingImmediatePayment = value;
        },
        setOverrideFields(state, payload) {
            Object.keys(payload).forEach((key) => {
                state.overrideFields[key] = payload[key];
            });
        },
        setOrderCustomFields(state, payload) {
            Object.keys(payload).forEach((key) => {
                state.customOrderFields[key] = payload[key];
            });
        },
        setLastCheckoutPaymentMethod(state, payment_method) {
            state.lastCheckoutPaymentMethod = payment_method;
        },
        salesButtonSeen(state) {
            state.salesButtonSeen = true;
        },
        salesButtonVisible(state, value) {
            state.salesButtonVisible = value;
        },
        setCurrency(state, payload) {
            state.currency = payload.currency;
            state.enforceHardDisplay = payload.enforceHardDisplay;
        },
        setSessionId(state, sessionId) {
            state.sessionId = sessionId;
        },
        setCollectedLead(state, payload) {
            state.collectedLead = payload;
        },
        setIsSca(state, payload) {
            state.is_sca = payload;
        },
        increaseCheckoutPaymentAttempts(state) {
            state.checkoutPaymentAttempts += 1;
        },
        increaseImmediatePaymentAttempts(state) {
            state.immediatePaymentAttempts += 1;
        },
        increaseUpdatePaymentMethodAttempts(state) {
            state.updatePaymentMethodAttempts += 1;
        },
        setIsRetryAttempt(state, payload) {
            state.retryAttempt = payload;
        },
        setContact(state, payload) {
            state.contact = payload;
            window.App.contact = payload;
        },
        setCardDetails(state, payload) {
            state.countryOfIssuance = payload.countryOfIssuance;
            state.issuingBank = payload.issuingBank;
            state.cardType = payload.cardType;
            state.paymentMethodToken = payload.paymentMethodToken;
        },
        setPaymentMethod(state, payload) {
            state.defaultPaymentMethod = payload.defaultPaymentMethod;
            state.paymentMethodToken = payload.paymentMethodToken;
        },
        setVerifyDetails(state, payload) {
            state.liabilityShifted = payload.liabilityShifted;
            state.liabilityShiftPossible = payload.liabilityShiftPossible;
            state.verifyStatus = payload.verifyStatus;
        },
        setLifetimeUpdates(state, payload) {
            state.lifetimeUpdates = payload;
        },
        setIosBackButtonVisible(state, value) {
            state.isIosBackButton = value;
        },
        setBraintreeDeviceData(state, value) {
            state.braintreeDeviceData = value;
        },
        setRemainingSpots(state, value) {
            state.remainingSpots = value;
        },
    },
    actions: {
        async postCheckoutStarted({ state, getters, commit }) {
            logger.addCheckoutBreadcrumb({ message: msg.checkout.setupStarting });

            if (state.collectedLead.email === state.emailInput) {
                return {
                    already_purchased: state.collectedLead.already_purchased,
                };
            }

            let csrf_fields = {};
            try {
                csrf_fields = await createToken('checkout-started', '.checkout__content', state.sessionId);
            } catch (err) {
                logger.captureException(new Error(msg.checkout.csrfTokenCallFailed), { extra: { err } });
                return;
            }

            try {
                let response = await axios.post(
                    '/api/checkout-started',
                    Object.assign({}, csrf_fields, {
                        email: state.emailInput,
                        product: getters.product.slug,
                        checkout_tag: getters.product.checkout_tag,
                        original_url: state.originalUrl,
                        device_data: state.device_data,
                        braintree_device_data: state.braintreeDeviceData,
                        psh: state.sessionId,
                        ref: state.referer,
                    }),
                    {
                        headers: {
                            'x-transaction-id': logger.getTransactionId(),
                        },
                    },
                );

                if (response.data.success) {
                    commit('setCollectedLead', {
                        email: state.emailInput,
                        already_purchased: response.data.already_purchased,
                    });

                    commit('setContact', {
                        email: response.data.email,
                        ips_id: response.data.uid,
                    });

                    logger.addCheckoutBreadcrumb({ message: msg.checkout.setupComplete });
                }
                return response.data;
            } catch (err) {
                if (err.response.status == 422 && err.response.data.errors.email) {
                    logger.addCheckoutBreadcrumb({
                        message: msg.checkout.serverEmailValidationFailed,
                        data: { err },
                    });

                    return err.response.data;
                }

                logger.captureException(new Error(msg.checkout.setupFailed), {
                    extra: {
                        error: err,
                        product: getters.product.slug,
                        checkout_tag: getters.product.checkout_tag,
                        psh: state.sessionId,
                    },
                });
            }
        },
        async checkoutPayment({ commit, getters, state, dispatch }, token) {
            // In case if default payment method is Apple Pay we call the AP payment as immediate payment
            // and need to show the full screen processing layer

            logger.addCheckoutBreadcrumb({ message: msg.checkout.paymentStartProcessing });

            if (state.popup.isOpen) {
                commit('setProcessingCheckout', true);
            } else {
                commit('setProcessingImmediatePayment', true);
            }

            let fields = {};
            try {
                fields = await createToken('checkout-payment', '.checkout__content', state.sessionId);
            } catch (err) {
                logger.captureException(new Error(msg.checkout.paymentCsrfTokenCallFailed), { extra: { err } });

                window.ipse.dispatch({
                    action: 'create_csrf_token_failed',
                    data_event: {
                        error: err,
                    },
                });

                commit('setIsRetryAttempt', false);

                if (state.popup.isOpen) {
                    commit('setProcessingCheckout', false);
                } else {
                    commit('setProcessingImmediatePayment', false);
                }
                commit('openCheckout', {
                    errorCode: 'system-error',
                    step: 2,
                });
                return;
            }

            const fullFormData = Object.assign({}, getters.formData, { token, purchase_flow: 'checkout' }, fields);

            let response = {};
            try {
                response = await axios.post('/api/checkout-payment', fullFormData, {
                    headers: {
                        'x-transaction-id': logger.getTransactionId(),
                    },
                });
            } catch (err) {
                const eventData = {
                    type: 'checkout',
                    event: 'payment_failed',
                    attempt: state.checkoutPaymentAttempts,
                    is_sca: state.is_sca,
                    currency: state.currency,
                    payment_type: state.paymentType,
                    payment_method: state.lastCheckoutPaymentMethod,
                    email: state.emailInput,
                    error_code: err.error_code,
                    error: err,
                };

                logger.captureException(new Error(msg.checkout.paymentFailed), {
                    extra: eventData,
                });

                window.ipse.dispatch({
                    action: 'purchase_failed',
                    data_event: eventData,
                });

                if (!state.is_sca && state.lastCheckoutPaymentMethod === 'credit-card') {
                    commit('setIsSca', true);
                    window.eventBus.emit('RETRY_CHECKOUT_PAYMENT_WITH_3D_SECURE');
                    return;
                }

                commit('setIsRetryAttempt', false);

                if (state.popup.isOpen) {
                    commit('setProcessingCheckout', false);
                } else {
                    commit('setProcessingImmediatePayment', false);
                }
                let error_code = generateErrorCode('checkout', state.lastCheckoutPaymentMethod, err);

                commit('openCheckout', {
                    errorCode: error_code,
                    step: error_code === 'already-purchased' ? 1 : 2,
                });

                return;
            }

            logger.addCheckoutBreadcrumb({ message: msg.checkout.paymentProcessed });

            const tracker = new Tracker();

            tracker
                .firePurchaseEvent(response.data.tracking_price, response.data.pixel_event_id, response.data.currency)
                .finally(() => {
                    if (response.data.lifetime_updates) {
                        commit('setProcessingImmediatePayment', false);
                        commit('setLifetimeUpdates', {
                            productSlug: response.data.lifetime_updates,
                            redirectUrl: window.ipse.redirectUrl(response.data.redirect_url),
                        });
                        commit('openCheckout', {
                            step: 3,
                        });
                    } else {
                        if (state.lifetimeUpdates?.standalone) {
                            commit('setProcessingCheckout', false);
                            commit('setLifetimeUpdates', {
                                thankYou: true,
                            });

                            commit('openCheckout', {
                                step: 3,
                            });

                            return;
                        }
                        window.ipse.redirect(response.data.redirect_url);
                    }
                });
        },
        async immediatePayment({ getters, state, commit, dispatch }, token) {
            logger.addCheckoutBreadcrumb({ message: msg.checkout.immediatePaymentStartProcessing });

            if (!state.lifetimeUpdates) commit('setProcessingImmediatePayment', true);

            let fields = {};

            try {
                fields = await createToken(
                    'immediate-payment',
                    state.lifetimeUpdates ? '.checkout__content' : '.processing_pop_up',
                    state.sessionId,
                );
            } catch (err) {
                logger.captureException(new Error(msg.checkout.immediatePaymentTokenFailed), { extra: err });

                window.ipse.dispatch({
                    action: 'create_csrf_token_failed',
                    data_event: {
                        error: err,
                    },
                });

                commit('setIsRetryAttempt', false);

                if (state.popup.isOpen) {
                    commit('setProcessingCheckout', false);
                } else {
                    commit('setProcessingImmediatePayment', false);
                }
                commit('openCheckout', {
                    errorCode: 'system-error',
                    step: 2,
                });
                return;
            }

            return axios
                .post(
                    '/api/immediate-payment',
                    Object.assign({}, getters.formData, { token, purchase_flow: 'immediate' }, fields),
                    {
                        headers: {
                            'x-transaction-id': logger.getTransactionId(),
                        },
                    },
                )
                .then((response) => {
                    const tracker = new Tracker();

                    tracker
                        .firePurchaseEvent(
                            response.data.tracking_price,
                            response.data.pixel_event_id,
                            response.data.currency,
                        )
                        .finally(() => {
                            if (response.data.lifetime_updates) {
                                commit('setProcessingImmediatePayment', false);
                                commit('setLifetimeUpdates', {
                                    productSlug: response.data.lifetime_updates,
                                    redirectUrl: window.ipse.redirectUrl(response.data.redirect_url),
                                });
                                commit('openCheckout', {
                                    step: 3,
                                });
                            } else {
                                if (state.lifetimeUpdates?.standalone) {
                                    commit('setProcessingCheckout', false);
                                    commit('setLifetimeUpdates', {
                                        thankYou: true,
                                    });

                                    return;
                                }
                                if (state.lifetimeUpdates && !state.lifetimeUpdates.standalone) {
                                    return window.ipse.redirect(state.lifetimeUpdates.redirectUrl);
                                }
                                window.ipse.redirect(response.data.redirect_url);
                            }
                        });

                    logger.addCheckoutBreadcrumb({ message: msg.checkout.immediatePaymentProcessed });
                })
                .catch((err) => {
                    logger.captureException(new Error(msg.checkout.immediatePaymentFailed), {
                        extra: {
                            type: 'immediate',
                            event: 'payment_failed',
                            attempt: state.immediatePaymentAttempts,
                            is_sca: state.is_sca,
                            currency: state.currency,
                            payment_type: state.paymentType,
                            payment_method: state.defaultPaymentMethod,
                            email: state.emailInput,
                            error_code: err.error_code,
                            error: err,
                        },
                    });

                    window.ipse.dispatch({
                        action: 'purchase_failed',
                        data_event: {
                            purchase_flow: 'immediate',
                            error: err,
                        },
                    });

                    if (!state.is_sca && state.defaultPaymentMethod === 'credit-card') {
                        commit('setIsSca', true);
                        window.eventBus.emit('RETRY_IMMEDIATE_PAYMENT_WITH_3D_SECURE');
                        return;
                    }
                    commit('setIsRetryAttempt', false);
                    commit('setProcessingImmediatePayment', false);
                    commit('setProcessingCheckout', false);
                    let error_code = generateErrorCode('immediate', state.defaultPaymentMethod, err);
                    commit('openCheckout', {
                        errorCode: error_code,
                        step: error_code === 'already-purchased' ? 1 : state.lifetimeUpdates ? 3 : 2,
                    });
                });
        },

        async updatePaymentMethod({ commit, state, dispatch }, { responseData }) {
            logger.addCheckoutBreadcrumb({ message: msg.checkout.updatePaymentMethodStartProcessing });

            commit('setProcessingCheckout', true);

            try {
                for (let i = 0; i < responseData.subscriptions.length; i++) {
                    let csrf_fields = {};
                    try {
                        csrf_fields = await createToken('update-payment-method', '.checkout__content', state.sessionId);
                    } catch (err) {
                        logger.captureException(new Error(msg.checkout.updatePaymentMethodCsrfTokenCallFailed), {
                            extra: { err },
                        });

                        window.ipse.dispatch({
                            action: 'create_csrf_token_failed',
                            data_event: {
                                error: err,
                            },
                        });

                        commit('setIsRetryAttempt', false);

                        commit('setProcessingCheckout', false);
                        commit('openCheckout', {
                            errorCode: 'system-error',
                            step: 2,
                        });
                        return;
                    }

                    let verifiedNonce = responseData.nonce;

                    let post_data = Object.assign(
                        {},
                        {
                            email: state.contact.email,
                            nonce: verifiedNonce,
                            subscription: responseData.subscriptions[i].id,
                            braintree_device_data: state.braintreeDeviceData,
                        },
                        csrf_fields,
                    );

                    let response = await axios.post('/api/update-payment-method', post_data, {
                        headers: {
                            'x-transaction-id': logger.getTransactionId(),
                        },
                    });

                    logger.addCheckoutBreadcrumb({ message: msg.checkout.updatePaymentMethodProcessed });

                    responseData.nonce = response.data.nonce;
                }

                window.ipse.dispatch({
                    action: 'update_payment_method_succeeded',
                    data_event: {
                        purchase_flow: 'update_payment_method',
                    },
                });

                window.ipse.redirect('/update-payment-method/thank-you');
            } catch (err) {
                commit('setProcessingCheckout', false);

                window.ipse.dispatch({
                    action: 'update_payment_method_failed',
                    data_event: {
                        purchase_flow: 'update_payment_method',
                        error: err,
                    },
                });

                logger.captureException(new Error(msg.checkout.updatePaymentMethodFailed), { extra: { err } });

                if (!state.is_sca && state.lastCheckoutPaymentMethod === 'credit-card') {
                    commit('setIsSca', true);
                    window.eventBus.emit('RETRY_UPDATE_PAYMENT_WITH_3D_SECURE');
                    return;
                }

                commit('setIsRetryAttempt', false);

                commit('openCheckout', {
                    errorCode: generateErrorCode('checkout', state.lastCheckoutPaymentMethod, err),
                });
            }
        },

        showPaymentErrorModal({ state, commit }, payment_method) {
            let payment_type = '';
            commit('setProcessingCheckout', false);

            if (payment_method === 'apple-pay') {
                payment_type = 'checkout';
            } else {
                payment_type = state.isUpsell ? 'immediate' : 'checkout';
            }

            commit('openCheckout', {
                errorCode: payment_type + '-' + payment_method + '-error',
                step: 2,
            });
        },
    },
});

function generateErrorCode(payment_source, payment_method, err) {
    let error_code = err.error_code;

    if (error_code === 'already-purchased') {
        return error_code;
    } else if (error_code === 'payment-error') {
        return payment_source + '-' + payment_method + '-error';
    } else if (error_code === 'insufficient-funds') {
        return 'insufficient-funds';
    } else {
        console.error(payment_source + ' - ' + payment_method + ' axios call error', err);
        // either system-error or undefined (failed before php)
        return 'system-error';
    }
}

export default store;
